import {from, Observable} from 'rxjs';
import {debounceTime, filter, map, switchMap} from 'rxjs/operators';

import IErrorsPayload                from '../../../Player/Interfaces/IErrorsPayload';
import ErrorType                     from '../../../Shared/Constants/ErrorType';
import Playback                      from '../../../State/Stores/Playback/Playback';
import getIsConnectionOk             from '../../Shared/Util/getIsConnectionOk';
import getHasSegmentSucceeded        from '../Abr/getHasSegmentSucceeded';
import getLastSegmentUrlFromManifest from '../Abr/getLastSegmentUrlFromManifest';

const MAX_BUFFERING_DURATION_BEFORE_ERROR = 5000;

/**
 * Runs a series of heuristics-based tests at a connection, manifest and segment
 * level in order to make an assumption about the cause of prolonged buffering,
 * returning the most likely error type.
 */

const investigateBuffering = async (playback: Playback): Promise<ErrorType> => {
    const isConnectionOk = await getIsConnectionOk();

    if (!isConnectionOk) {
        return ErrorType.PLAYBACK_INTERRUPTED_CONNECTION_LOST;
    }

    let lastSegmentUrl = '';

    try {
        lastSegmentUrl = await getLastSegmentUrlFromManifest(playback);
    } catch (err) {
        return playback.hasPlayed ?
            ErrorType.MANIFEST_REQUEST_FAILED_DURING_PLAYBACK :
            ErrorType.MANIFEST_REQUEST_FAILED_DURING_LOAD;
    }

    const hasSegmentSucceeded = await getHasSegmentSucceeded(lastSegmentUrl);

    if (!hasSegmentSucceeded) {
        return playback.hasPlayed ?
            ErrorType.SEGMENT_REQUEST_FAILED_DURING_PLAYBACK :
            ErrorType.SEGMENT_REQUEST_FAILED_DURING_LOAD;
    }

    return ErrorType.NONE;
};

const createInvestigateBufferingStream = (latestPlaybackValue: Playback): Observable<ErrorType> =>
    from(investigateBuffering(latestPlaybackValue));

/**
 * Creates a stream of errors relating to interrupted playback. Runs heuristics
 * after a period of 5 seconds of uninterrupted buffering.
 */

const createPlaybackErrorsStream = (
    playbackStoreUpdate$: Observable<Playback>
): Observable<IErrorsPayload> =>
    playbackStoreUpdate$
        .pipe(
            // Ensure 5000ms with no emissions has elapsed before emitting
            debounceTime(MAX_BUFFERING_DURATION_BEFORE_ERROR),
            // Emit only if buffering after 5000ms of inactivity
            filter(({isBuffering}) => isBuffering),
            // Map emissions to a new stream, emitting once an investigation
            // is completed
            switchMap(latestPlaybackValue => createInvestigateBufferingStream(latestPlaybackValue)),
            // Filter out emissions if the investigation yields no error
            filter(errorType => errorType !== ErrorType.NONE),
            // Map to payload
            map(errorType => ({
                type: errorType,
                source: null
            }))
        );

export default createPlaybackErrorsStream;